import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Input from "src/components/inputs/Input";
import SearchList from "src/components/search-list/SearchList";
import ToggleWithLabel from "src/components/toggles/Toggle";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import useUserPlace from "src/hooks/useUserPlace";
import { getUserStoreRequest, setActiveStore } from "src/redux/actions/app";
import { isSuperAdmin } from "src/redux/selectors/app";
import { ADDRESS } from "src/utils/constants";
import { number, object, string } from "yup";
import {
  useCreateStore,
  useGetStoreData,
  useUpdateStore,
} from "../api-clients";

const INDIA_COUNTRY_CODE = "IN";

const FORM_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  GST: "gst",
  PHONE: "phone",
  ALTERNATE_PHONE: "alternatePhone",
  FAX: "fax",
  ADDRESS: "address",
  CITY: "city",
  STATE: "state",
  COUNTRY: "country",
  STATUS: "status",
};

const CreateStore = ({
  isInViewMode = false,
  onSuccess,
  onCancel,
}: {
  isInViewMode?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}) => {
  useSetActiveTab(TAB_NAMES.STORES);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { storeId } = useParams();

  const id = Number(storeId);

  const isInEditMode = useMemo(() => !!id, [id]);

  const superAdmin = useSelector(isSuperAdmin);

  const {
    isLoading: isGetStoreDataLoading,
    data,
    mutate,
  } = useGetStoreData(id);

  const storeData = useMemo(() => data?.data, [data?.data]);

  const { errorNotification, successNotification } = useNotification();

  const { getUserPlaceByType, handleSearchByType } = useUserPlace();

  const {
    isLoading: isUpdateLoading,
    execute: updateStore,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateStore(id);

  const {
    isLoading,
    data: response,
    isSuccess,
    execute,
    error,
  } = useCreateStore();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: storeData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.EMAIL]: storeData?.[FORM_FIELDS.EMAIL] ?? "",
      [FORM_FIELDS.GST]: storeData?.[FORM_FIELDS.GST] ?? "",
      [FORM_FIELDS.PHONE]: storeData?.[FORM_FIELDS.PHONE] ?? null,
      [FORM_FIELDS.ALTERNATE_PHONE]:
        storeData?.[FORM_FIELDS.ALTERNATE_PHONE] ?? null,
      [FORM_FIELDS.FAX]: storeData?.[FORM_FIELDS.FAX] ?? "",
      [FORM_FIELDS.ADDRESS]: storeData?.[FORM_FIELDS.ADDRESS] ?? "",
      [FORM_FIELDS.CITY]: storeData?.[FORM_FIELDS.CITY] ?? "",
      [FORM_FIELDS.STATE]: storeData?.[FORM_FIELDS.STATE] ?? "",
      [FORM_FIELDS.COUNTRY]: storeData?.[FORM_FIELDS.COUNTRY] ?? "",
      [FORM_FIELDS.STATUS]: storeData?.[FORM_FIELDS.STATUS] ?? false,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.PHONE]: number().nullable(),
      [FORM_FIELDS.ALTERNATE_PHONE]: number().nullable(),
      [FORM_FIELDS.EMAIL]: string().email("Please enter valid email."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateStore(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(isUpdateSuccess ? "Store Updated" : "Store Created.");

      if (superAdmin) {
        return navigate("/stores");
      } else {
        dispatch(getUserStoreRequest());

        if (isSuccess && response?.storeId) {
          dispatch(setActiveStore(response.storeId));
        }
      }
      onSuccess?.();
    }
  }, [
    dispatch,
    isInEditMode,
    isSuccess,
    isUpdateSuccess,
    navigate,
    onSuccess,
    response?.storeId,
    successNotification,
    superAdmin,
  ]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const countryValue = useMemo(() => values[FORM_FIELDS.COUNTRY], [values]);

  const isInLoading = useMemo(
    () => isLoading || isUpdateLoading,
    [isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading || isGetStoreDataLoading,
    [isGetStoreDataLoading, isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full w-full">
      <TabHeader label={id ? "Store" : "Create Store"} />
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <Input
            label="Store Name"
            placeholder="Store Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isFieldDisabled}
          />
          <Input
            type="email"
            label="Email"
            placeholder="Email"
            name={FORM_FIELDS.EMAIL}
            value={values[FORM_FIELDS.EMAIL] as string}
            error={getFieldError(FORM_FIELDS.EMAIL)}
            onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Input
            label="GST Number"
            placeholder="GST Number"
            name={FORM_FIELDS.GST}
            value={values[FORM_FIELDS.GST] as string}
            error={getFieldError(FORM_FIELDS.GST)}
            onChange={(e) => setFieldValue(FORM_FIELDS.GST, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Input
            type="number"
            label="Phone Number"
            placeholder="Phone Number"
            name={FORM_FIELDS.PHONE}
            value={values[FORM_FIELDS.PHONE] as string}
            error={getFieldError(FORM_FIELDS.PHONE)}
            onChange={(e) => setFieldValue(FORM_FIELDS.PHONE, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Input
            type="number"
            label="Alternate Phone"
            placeholder="Alternate Phone"
            name={FORM_FIELDS.ALTERNATE_PHONE}
            value={values[FORM_FIELDS.ALTERNATE_PHONE] as string}
            error={getFieldError(FORM_FIELDS.ALTERNATE_PHONE)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.ALTERNATE_PHONE, e.target.value)
            }
            disabled={isFieldDisabled}
          />
          <Input
            label="Fax Number"
            placeholder="Fax Number"
            name={FORM_FIELDS.FAX}
            value={values[FORM_FIELDS.FAX] as string}
            error={getFieldError(FORM_FIELDS.FAX)}
            onChange={(e) => setFieldValue(FORM_FIELDS.FAX, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Input
            label="Address"
            placeholder="Address"
            name={FORM_FIELDS.ADDRESS}
            value={values[FORM_FIELDS.ADDRESS] as string}
            error={getFieldError(FORM_FIELDS.ADDRESS)}
            onChange={(e) => setFieldValue(FORM_FIELDS.ADDRESS, e.target.value)}
            disabled={isFieldDisabled}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[30px]">
            <SearchList
              label="City"
              placeholder="Enter City"
              onSearch={(val) => handleSearchByType(val, ADDRESS.CITY)}
              value={getUserPlaceByType(values[FORM_FIELDS.CITY], ADDRESS.CITY)}
              onChange={(val) => setFieldValue(FORM_FIELDS.CITY, val)}
              disabled={isFieldDisabled}
              inputClassName="rounded-[5px]"
            />
            {[INDIA_COUNTRY_CODE, ""].includes(countryValue as string) && (
              <>
                <SearchList
                  label="State"
                  placeholder="Enter State"
                  onSearch={(val) => handleSearchByType(val, ADDRESS.STATE)}
                  value={getUserPlaceByType(
                    values[FORM_FIELDS.STATE],
                    ADDRESS.STATE
                  )}
                  onChange={(val) => setFieldValue(FORM_FIELDS.STATE, val)}
                  disabled={isFieldDisabled}
                  inputClassName="rounded-[5px]"
                />
                <SearchList
                  label="Country"
                  placeholder="Enter Country"
                  onSearch={(val) => handleSearchByType(val, ADDRESS.COUNTRY)}
                  value={getUserPlaceByType(
                    values[FORM_FIELDS.COUNTRY],
                    ADDRESS.COUNTRY
                  )}
                  onChange={(val) => setFieldValue(FORM_FIELDS.COUNTRY, val)}
                  disabled={isFieldDisabled}
                  inputClassName="rounded-[5px]"
                />
              </>
            )}
          </div>
          <ToggleWithLabel
            label="Status"
            checked={values[FORM_FIELDS.STATUS] as boolean}
            onToggle={(val) => [setFieldValue(FORM_FIELDS.STATUS, val)]}
            disabled={isFieldDisabled}
            showToggleStatus
          />
        </div>
        <div className="flex gap-3">
          {onCancel && (
            <Button
              text={"Cancel"}
              onClick={onCancel}
              danger
              className="h-[40px] px-10"
              isDisabled={isFieldDisabled}
            />
          )}
          <Button
            text={isInLoading ? "Loading..." : "Submit"}
            onClick={handleSubmit}
            primary
            className="h-[40px] px-10"
            isDisabled={isFieldDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateStore;
